import { CompanyMeasurementDateModel, GPC, ValuationApproachDCF } from 'api';
import { VALUATIONS_PERCENTILE_PATTERN as patterns } from 'common/constants/valuations';
import { ValuationsApproach } from 'common/types/valuation';
import { PUBLIC_COMPANIES, PUBLIC_TRANSACTIONS } from 'pages/Valuations/util/constants';
import { matchPattern } from 'pages/ValuationsAllocation/util';
import { Logger } from 'utilities';
import { ExtendedValuationApproachModel } from '../approaches/backsolveApproach/types';
import { ComparisonAttrs } from '../approaches/guidelinePublicCompanies/types';

export const getWaccDataApproach = (approach: ValuationsApproach, approachName: string) => {
  if (!(approachName in approach)) return null;

  const { wacc_cost_of_equity, wacc_cost_of_debt, wacc_invested_capital_debt, wacc_invested_capital_equity, wacc }
    = approach[approachName as keyof ValuationsApproach] as ValuationApproachDCF;

  return {
    wacc_cost_of_equity,
    wacc_cost_of_debt,
    wacc_invested_capital_debt,
    wacc_invested_capital_equity,
    wacc,
  };
};

export const getNolDataApproach = (approach: ValuationsApproach, approachName: string) => {
  if (!(approachName in approach)) return null;

  const { nol_ebit_pre_tax_cut, nol_ebit_post_tax_cut, nol_present_value_savings } = approach[
    approachName as keyof ValuationsApproach
  ] as ValuationApproachDCF;

  return {
    nol_ebit_pre_tax_cut,
    nol_ebit_post_tax_cut,
    nol_present_value_savings,
  };
};

export const getThresholdDate = (companyMeasurementDate?: CompanyMeasurementDateModel | null) => {
  if (companyMeasurementDate?.company) {
    const [mdYear, mdMonth, mdDay] = companyMeasurementDate.measurement_date?.date?.split('-') || [];
    const { fiscal_year_end } = companyMeasurementDate.company;
    const auxYear = mdYear ? Number(mdYear) - 1 : null;

    const auxDate = `${auxYear}-${mdMonth}-${mdDay}`;
    return fiscal_year_end?.end_month === Number(mdMonth) && fiscal_year_end?.end_day === Number(mdDay)
      ? companyMeasurementDate.measurement_date?.date
      : auxDate;
  }
};

type TrimUpdateFieldsParams = {
  comparison: GPC;
  fieldAttributes: ComparisonAttrs;
  fieldList: string[];
};

export const trimUpdateFields = ({ comparison, fieldAttributes, fieldList }: TrimUpdateFieldsParams) =>
  fieldList.reduce((updated: any, field) => {
    const decimalPlaces = fieldAttributes[field as keyof ComparisonAttrs]?.decimal_places ?? 2;
    // eslint-disable-next-line no-param-reassign
    updated[field] = parseFloat(parseFloat(comparison[field as keyof GPC]).toFixed(decimalPlaces));
    return updated;
  }, {});

export const getFixedValue = (value: string | number, decimalPlaces: number ) => {
  try {
    const valueType = typeof value;
    return valueType === 'string' ? Number(value).toFixed(decimalPlaces) : (value as number).toFixed(decimalPlaces);
  } catch (e) {
    Logger.error('Error getting fixed value', e);
    return value;
  }
};
// expressions generators for row configs
const emptyRowRange = '[]';

export const getForwardMultipleDiscountExpression = ({
  rowRange,
  numeratorColumnLegend,
  denominatorColumnLegend,
  startWithEqual = true,
}: {
  rowRange: string;
  numeratorColumnLegend: string;
  denominatorColumnLegend: string;
  startWithEqual?: boolean;
}) => {
  const ntmRowRange = rowRange.replace(/@/g, numeratorColumnLegend);
  const ltmRowRange = rowRange.replace(/@/g, denominatorColumnLegend);
  
  return startWithEqual
    ? `=MEAN(FILTER_ENABLED(${ntmRowRange}))/MEAN(FILTER_ENABLED(${ltmRowRange}))-1`
    : `MEAN(FILTER_ENABLED(${ntmRowRange}))/MEAN(FILTER_ENABLED(${ltmRowRange}))-1`;
};

export const getMedianExpression = (rowRange: string, startWithEqual = true) =>
  startWithEqual ? `=MEDIAN(FILTER_ENABLED(${rowRange}))` : `MEDIAN(FILTER_ENABLED(${rowRange}))`;

export const getMeanExpression = (rowRange: string, startWithEqual = true) =>
  startWithEqual ? `=MEAN(FILTER_ENABLED(${rowRange}))` : `MEAN(FILTER_ENABLED(${rowRange}))`;

export const get75thPercentileExpression = (rowRange: string, startWithEqual = true) =>
  startWithEqual ? `=PERCENTILE(FILTER_ENABLED(${rowRange}), 75)` : `PERCENTILE(FILTER_ENABLED(${rowRange}), 75)`;

export const get25thPercentileExpression = (rowRange: string, startWithEqual = true) =>
  startWithEqual ? `=PERCENTILE(FILTER_ENABLED(${rowRange}), 25)` : `PERCENTILE(FILTER_ENABLED(${rowRange}), 25)`;

export const getPercentileExpression = (rowRange: string, percentile: string, startWithEqual = true) =>
  startWithEqual
    ? `=PERCENTILE(FILTER_ENABLED(${rowRange}), ${percentile})`
    : `PERCENTILE(FILTER_ENABLED(${rowRange}), ${percentile})`;

type GetAppliedMultipleExpressionParams = {
  offset: number;
  comparisonsLength: number;
  variableExpressions: string[];
};

export const getAppliedMultipleExpression = ({ offset, comparisonsLength, variableExpressions }: GetAppliedMultipleExpressionParams) =>
  `=CHOOSE(@${offset + comparisonsLength}, ${variableExpressions})`;

export const getCustomPercentileExpression = (rowRange: string, percentile: string, startWithEqual = true) => {
  if (!percentile) return '';
  const percentileValue = percentile.replace(/\D/g, '');
  return startWithEqual
    ? `=PERCENTILE(FILTER_ENABLED(${rowRange}), ${percentileValue})`
    : `PERCENTILE(FILTER_ENABLED(${rowRange}), ${percentileValue})`;
};


type GetVariableExpressionsParams = {
  companiesRange: string;
  currentSelectionOptions: string[];
  numberOfCompanies: number;
  replaceSymbol?: string;
  forDCF?: boolean;
  benchmarkType?: string;
};
export const getVariableExpressions = ({
  companiesRange,
  currentSelectionOptions,
  numberOfCompanies,
  replaceSymbol = '@',
  forDCF = false,
  benchmarkType = '',
}: GetVariableExpressionsParams) => {
  if (companiesRange === emptyRowRange) {
    return [0, 0, 0, 0];
  }
  const optionsLength = currentSelectionOptions.length;
  // when obtaining expressions for DCF we need to consider an extra option
  const endOfSlice = forDCF ? optionsLength - 2 : optionsLength - 1;
  const percentileOptions = currentSelectionOptions.slice(2, endOfSlice);
  const medianExpr = getMedianExpression(companiesRange, false);
  const meanExpr = getMeanExpression(companiesRange, false);
  const percentilesExpressions = percentileOptions.map(option => {
    const matchedPattern = matchPattern({
      label: option,
      patterns,
    });
    const percentile = option.split(matchedPattern)[0];
    return getPercentileExpression(companiesRange, percentile, false);
  });
  const specifiedCellOffset = forDCF && benchmarkType === PUBLIC_TRANSACTIONS ? 10 : 9;
  const specifiedCellExpression = `${replaceSymbol}${numberOfCompanies + specifiedCellOffset}`;
  return [medianExpr, meanExpr, ...percentilesExpressions, specifiedCellExpression];
};

type ExtractGPCApproachesParams = {
  approaches: ExtendedValuationApproachModel[];
};

export const extractGPCApproaches = ({ approaches }: ExtractGPCApproachesParams) =>
  approaches
    .filter(({ approach_type }) => approach_type === PUBLIC_COMPANIES)
    .map(({ panelId, valuations_approach_gpc, name }) => ({
      value: valuations_approach_gpc?.id ?? panelId,
      label: name,
    }));
